import { compose, lifecycle, withHandlers, withStateHandlers } from 'recompose';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { sortBy } from 'lodash';

import { addIntlProps, addProps } from 'utils/recompose';
import combineSelectors from 'utils/combineSelectors';
import formatPipelineKeys from 'utils/formatPipelineKeys';
import formatCurrencyGeneral from 'utils/formatCurrencyGeneral';

import {
  fetchModelAdvanced,
  clearAdvancedFilters,
} from 'reducers/modelAdvanced/actions';
import { advancedData } from 'reducers/modelAdvanced/selectors';
import { pipelineData } from 'reducers/modelPipeline/selectors';
import { theme } from 'reducers/dashboard/selectors';
import { fetchModelPipeline } from 'reducers/modelPipeline/actions';
import { isInitialState } from 'utils/isInitialState';
import { setObjValuesToFalse } from 'utils/setObjValuesToFalse';

import messages from './messages';
import styles from './styles.module.scss';

const mapStateToProps = combineSelectors({
  advancedData,
  pipelineData,
  theme,
});

const mapDispatchToProps = {
  clearAdvancedFilters,
  fetchModelAdvanced,
  fetchModelPipeline,
};

const initialStateKeys = {
  showPresold: 'showPresold',
  showStock: 'showStock',
};

export function componentDidMount() {
  this.props.fetchModelAdvanced({ modelId: this.props.modelId });
  this.props.fetchModelPipeline({ modelId: this.props.modelId });
}

export const initialState = { showPresold: true, showStock: true };

export const togglePresold = props => () => {
  const { showPresold } = props;
  if (isInitialState(props)) {
    return setObjValuesToFalse(props, initialStateKeys.showPresold);
  }
  return { showPresold: !showPresold };
};

export const toggleStock = props => () => {
  const { showStock } = props;
  if (isInitialState(props)) {
    return setObjValuesToFalse(props, initialStateKeys.showStock);
  }
  return { showStock: !showStock };
};

export const clearToggles = () => () => initialState;

export const presoldFilterStyle = ({ showPresold }) =>
  showPresold ? styles.inactiveFilter : styles.activeFilter;

export const stockFilterStyle = ({ showStock }) =>
  showStock ? styles.inactiveFilter : styles.activeFilter;

export const presoldData = ({ advancedData }) => {
  const presold =
    advancedData &&
    advancedData.orderTypes &&
    advancedData.orderTypes.filter(
      orderType => orderType.type === 'Customer Sold',
    );
  return presold && presold.length > 0 ? presold[0] : null;
};

export const stockData = ({ advancedData }) => {
  const stock =
    advancedData &&
    advancedData.orderTypes &&
    advancedData.orderTypes.filter(
      orderType => orderType.type === 'Stock Order',
    );
  return stock && stock.length > 0 ? stock[0] : null;
};

export const currentPresoldQuantity = ({ presoldData }) =>
  (presoldData && presoldData.count) || 0;
export const currentStockQuantity = ({ stockData }) =>
  (stockData && stockData.count) || 0;

const populateWithStockData = (adDataObj, stockData) => {
  stockData &&
    stockData.darCategoryCounts.forEach(dar => {
      if (adDataObj[dar.darCategory.code]) {
        adDataObj[dar.darCategory.code].stock = dar.count;
      } else {
        adDataObj[dar.darCategory.code] = {
          name: dar.darCategory.description,
          stock: dar.count,
          presold: 0,
        };
      }
    });
};

const populateWithPresoldData = (adDataObj, presoldData) => {
  presoldData &&
    presoldData.darCategoryCounts.forEach(dar => {
      if (adDataObj[dar.darCategory.code]) {
        adDataObj[dar.darCategory.code].presold = dar.count;
      } else {
        adDataObj[dar.darCategory.code] = {
          name: dar.darCategory.description,
          presold: dar.count,
          stock: 0,
        };
      }
    });
};

const advanceChartData = pipelineData =>
  pipelineData &&
  pipelineData.sort((a, b) => a.code - b.code).reduce((obj, stage) => {
    obj[stage.code] = {
      name: formatPipelineKeys(stage.description),
      stock: 0,
      presold: 0,
    };
    return obj;
  }, {});

export const data = ({ pipelineData, presoldData, stockData }) => {
  let adDataObj = advanceChartData(pipelineData);
  adDataObj && populateWithStockData(adDataObj, stockData);
  adDataObj && populateWithPresoldData(adDataObj, presoldData);

  return adDataObj && Object.values(adDataObj);
};

export const stockColor = ({ theme }) => theme.charts.advanced.stock;
export const presoldColor = ({ theme }) => theme.charts.advanced.presold;

export const priceBands = ({ advancedData, currency, intl }) => {
  let priceBandData = [];

  advancedData &&
    advancedData.priceBands.forEach(pb => {
      let min = formatCurrencyGeneral(pb.priceBand.min, currency, intl);
      let max = formatCurrencyGeneral(pb.priceBand.max, currency, intl);

      priceBandData.push({
        id: pb.priceBand.id,
        priceRange: `${min} - ${max}`,
        count: pb.count,
      });
    });

  return sortBy(priceBandData, ['id']);
};

export const optionCodes = ({ advancedData }) =>
  advancedData && advancedData.functionalOptions;

export const paintOptions = ({ advancedData }) =>
  advancedData && advancedData.paintOptions;

export const interiorOptions = ({ advancedData }) =>
  advancedData && advancedData.interiorOptions;

export const resetFilters = ({
  clearAdvancedFilters,
  fetchModelAdvanced,
  modelId,
  clearToggles,
}) => () => {
  clearToggles();
  clearAdvancedFilters();
  fetchModelAdvanced({ modelId });
};

export default compose(
  injectIntl,
  addIntlProps({
    messages,
    propNames: ['presoldLabel', 'stockLabel', 'currency', 'clearFiltersLabel'],
  }),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  lifecycle({ componentDidMount }),
  withStateHandlers(initialState, {
    togglePresold,
    toggleStock,
    clearToggles,
  }),
  withHandlers({ resetFilters }),
  addProps({
    presoldFilterStyle,
    stockFilterStyle,
    presoldColor,
    stockColor,
    presoldData,
    stockData,
    priceBands,
    optionCodes,
    paintOptions,
    interiorOptions,
  }),
  addProps({
    data,
    currentStockQuantity,
    currentPresoldQuantity,
  }),
);
